/**
 * OCR pre-fill merge for the VOE call panel's verify form.
 *
 * The backend (`voe.extract_response_document`) extracts verification fields
 * from an uploaded VOE response document — the Tenstreet Xchange reply is the
 * canonical case — and the call-context payload carries them as
 * `response_extraction`. `applyResponseExtraction` merges those fields into
 * the verify form when it opens.
 *
 * The rules that keep the human in the loop:
 *  - ONLY empty fields are filled: '' strings and null booleans. The employer
 *    row's own stored values (which startVOECall applies first) outrank the
 *    extraction, and anything the operator typed outranks both.
 *  - The ternary confirms (datesConfirmed / positionConfirmed) are NEVER
 *    filled — the operator confirms the dates, the OCR only suggests them.
 *  - workRecordNotes is the operator's own record — never pre-filled.
 *
 * Pure by design (spec: tests/voe-response-extraction.spec.ts) — no Vue, no
 * services, no network.
 */

export interface VOEVerificationFormState {
  respondentName: string
  respondentTitle: string
  datesConfirmed: boolean | 'unable' | null
  employmentStart: string
  employmentEnd: string
  positionHeld: string
  positionConfirmed: boolean | 'unable' | null
  reasonForLeaving: string
  subjectToFmcsa: boolean | null
  hasAccidents: boolean | null
  accidents: string[]
  hasViolations: boolean | null
  violations: string[]
  eligibleForRehire: boolean | null
  subjectToDrugAlcoholTesting: boolean | null
  hasDrugAlcoholViolation: boolean | null
  drugAlcoholViolationDetails: string
  completedSapProgram: boolean | null
  wasDisqualified: boolean | null
  workRecordNotes: string
}

/** The canonical initial form — useVOECall resets to this on every open. */
export function emptyVerificationForm(): VOEVerificationFormState {
  return {
    respondentName: '',
    respondentTitle: '',
    datesConfirmed: null,
    employmentStart: '',
    employmentEnd: '',
    positionHeld: '',
    positionConfirmed: null,
    reasonForLeaving: '',
    subjectToFmcsa: null,
    hasAccidents: null,
    accidents: [],
    hasViolations: null,
    violations: [],
    eligibleForRehire: null,
    subjectToDrugAlcoholTesting: null,
    hasDrugAlcoholViolation: null,
    drugAlcoholViolationDetails: '',
    completedSapProgram: null,
    wasDisqualified: null,
    workRecordNotes: '',
  }
}

/** Only these keys are read from an extraction payload — anything else the
 *  document carried is already dropped by the backend parser, and is dropped
 *  here too as a second gate. */
const STRING_FIELDS = {
  respondent_name: 'respondentName',
  respondent_title: 'respondentTitle',
  employment_start: 'employmentStart',
  employment_end: 'employmentEnd',
  position_held: 'positionHeld',
  reason_for_leaving: 'reasonForLeaving',
  drug_alcohol_violation_details: 'drugAlcoholViolationDetails',
} as const

const BOOLEAN_FIELDS = {
  subject_to_fmcsa: 'subjectToFmcsa',
  has_accidents: 'hasAccidents',
  has_violations: 'hasViolations',
  eligible_for_rehire: 'eligibleForRehire',
  subject_to_drug_alcohol_testing: 'subjectToDrugAlcoholTesting',
  has_drug_alcohol_violation: 'hasDrugAlcoholViolation',
  completed_sap_program: 'completedSapProgram',
  was_disqualified: 'wasDisqualified',
} as const

/**
 * Merge OCR-extracted fields into the verify form, filling only empty slots.
 * Returns a NEW state object; the caller assigns it to `verification.value`.
 */
export function applyResponseExtraction(
  current: VOEVerificationFormState,
  extracted: Record<string, unknown> | null | undefined,
): VOEVerificationFormState {
  if (!extracted || typeof extracted !== 'object') return current

  // Key-oriented writes below; the map shape and the form shape are the same
  // object shape, so this cast is sound and contained to this function.
  const merged = { ...current } as unknown as Record<string, unknown>

  for (const [sourceKey, targetKey] of Object.entries(STRING_FIELDS)) {
    const value = extracted[sourceKey]
    const target = current[targetKey as keyof VOEVerificationFormState]
    if (typeof value === 'string' && value.trim() && typeof target === 'string' && target.trim() === '') {
      merged[targetKey] = value
    }
  }

  for (const [sourceKey, targetKey] of Object.entries(BOOLEAN_FIELDS)) {
    const value = extracted[sourceKey]
    if (typeof value === 'boolean' && current[targetKey as keyof VOEVerificationFormState] === null) {
      merged[targetKey] = value
    }
  }

  // Multi-line detail text pre-fills the per-item lists the save path joins
  // back together (accidents/violations ride the form as string lists).
  const detailsLists = {
    accident_details: 'accidents',
    violation_details: 'violations',
  } as const
  for (const [sourceKey, targetKey] of Object.entries(detailsLists)) {
    const value = extracted[sourceKey]
    const target = current[targetKey as keyof VOEVerificationFormState]
    if (typeof value === 'string' && value.trim() && Array.isArray(target) && isBlankList(target)) {
      const lines = value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
      if (lines.length) merged[targetKey] = lines
    }
  }

  return merged as unknown as VOEVerificationFormState
}

function isBlankList(list: string[]): boolean {
  return list.every((entry) => !entry || !entry.trim())
}